import React, { useEffect } from 'react';
import FocusTrap from 'focus-trap-react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Portal from './modal-portal';
import { ModalProvider } from './modal.context';

/**
 * @typedef Props
 * @prop {React.ReactNode} children
 * @prop {string} [className]
 * @prop {boolean} [show]
 * @prop {'default' | 'alt'} [variant]
 * @prop {'default' | 'slideout'} [placement]
 * @prop {() => void} [onClose]
 * @prop {'default' | 'sm' | 'md' | 'lg' | 'xl'} [size]
 * @prop {boolean} [takeover]
 * @prop {{}} [focusTrapOptions]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifModal = (props) => {
  const {
    children,
    className = '',
    show = false,
    variant = 'default',
    placement = 'default',
    onClose = null,
    size = 'default',
    takeover = false,
    focusTrapOptions = {},
    ...other
  } = props;
  const closeModal = () => {
    onClose();
    document.body.style.overflow = '';
  };

  const classes = classNames({
    'motif-modal': true,
    'motif-modal-content': true,
    'motif-modal-slideout': variant === 'slideout',
    'motif-modal-alt': variant === 'alt',
    'motif-modal-size-sm': !takeover && size === 'sm',
    'motif-modal-size-md': !takeover && (size === 'md' || size === 'default'),
    'motif-modal-size-lg': !takeover && size === 'lg',
    'motif-modal-size-xl': !takeover && size === 'xl',
    'motif-modal-takeover': takeover,
    [className]: true
  });

  useEffect(() => {
    document.body.style.overflow = show ? 'hidden' : '';
  }, [show]);

  const handleKeyDown = event => {
    switch (event.key) {
      case 'Escape':
        closeModal();
        break;

      default:
        break;
    }
  };

  return (
    <ModalProvider value={{ closeModal }}>
      {show && (
        <Portal>
          <div
            data-testid="motif-modal-overlay"
            className={`motif-modal-overlay ${takeover ? 'motif-modal-overlay-takeover' : ''}`}
            data-motif-modal
          >
            <FocusTrap
              focusTrapOptions={{
                preventScroll: true,
                ...focusTrapOptions
              }}
            >
              <div
                role="dialog"
                aria-modal="true"
                className={classes}
                onKeyDown={handleKeyDown}
                {...other}
              >
                {children}
              </div>
            </FocusTrap>
          </div>
        </Portal>
      )}
    </ModalProvider>
  );
};

MotifModal.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  placement: PropTypes.oneOf(['default', 'slideout']),
  variant: PropTypes.oneOf(['default', 'alt']),
  show: PropTypes.bool,
  onClose: PropTypes.func,
  size: PropTypes.oneOf(['default', 'sm', 'md', 'lg', 'xl']),
  takeover: PropTypes.bool,
  focusTrapOptions: PropTypes.objectOf(PropTypes.object)
};

export { MotifModal };
